import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { ActionRole, ButtonType, Level } from '@newroom-connect/library/enums';
import { FileHelper } from '@newroom-connect/library/helpers';
import { IFileUploadItem } from '@newroom-connect/library/interfaces';
import { TranslatePipe } from '@newroom-connect/library/pipes';

import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { InputTextComponent } from '../../inputs/input-text/input-text.component';
import { LoadingSpinnerComponent } from '../../loading/loading-spinner/loading-spinner.component';
import { MediaPreviewComponent } from '../../media-preview/media-preview.component';
import { NoteComponent } from '../../note/note.component';
import { ButtonComponent } from '../../buttons';

export interface IFileReplacerEvent {
  fileUploadItem: IFileUploadItem;
  trackingId: string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FileUploaderComponent,
    LoadingSpinnerComponent,
    NoteComponent,
    MediaPreviewComponent,
    InputTextComponent,
    TranslatePipe,
    ButtonComponent
  ],
  selector: 'nrc-file-replacer',
  templateUrl: './file-replacer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileReplacerComponent implements OnChanges {
  @Input() public fileTitle?: string;
  @Input() public fileMimetype?: string;
  @Input() public fileSource?: string;
  @Input() public fileThumbnail?: string;
  @Input() public fileDescription?: string;
  @Input() public isPdfGenerating = false;
  @Input() public isReplacementComplete = false;

  @Output() public fileChangedEvent = new EventEmitter<IFileReplacerEvent>();
  @Output() public fileSizeLimitExceededEvent = new EventEmitter<File>(); // Emit the file which exceeds the size limit.
  @Output() public uploadNewVersionEvent = new EventEmitter<void>();

  public acceptableMimeTypes?: string[];
  public buttonType = ButtonType;
  public level = Level;
  public actionRole = ActionRole;

  public fileTitleForm = new FormGroup({
    title: new FormControl({ value: '', disabled: true })
  });

  public isBackgroundThumbnailAnimatedSig = signal<boolean>(false);

  /**
   *
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileTitle']?.currentValue) {
      this.fileTitleForm.controls['title'].patchValue(changes['fileTitle']?.currentValue);
    }

    // Set the acceptable mimetypes for the file uploader based on the mimetype of the given file.
    if (changes['fileMimetype']?.currentValue) {
      const mimetypeGroup = FileHelper.extractMimeTypeGroup(changes['fileMimetype']?.currentValue);

      this.acceptableMimeTypes = mimetypeGroup ? [`${mimetypeGroup}/*`] : undefined;
    }

    if (!this.isBackgroundThumbnailAnimatedSig() && Object.keys(changes).every(changeKey => changes[changeKey].previousValue !== undefined)) {
      this.animateBackgroundThumbnail();
    }
  }

  /**
   * Handle file selection from the file uploader.
   * Generates a tracking ID and emits it along with the file upload item.
   *
   * @param fileUploadItems
   */
  public handleFileSelected(fileUploadItems: IFileUploadItem[]): void {
    if (fileUploadItems.length === 0) {
      return;
    }

    const trackingId = uuidv4();

    this.fileChangedEvent.emit({ fileUploadItem: fileUploadItems[0], trackingId });
  }

  /**
   * Animate the background thumbnail where the corresponding signal is set to `true` and then reset to `false` after a specified timeout.
   *
   * @param timeoutInMs The timeout im milliseconds to reset the animation signal.
   */
  private animateBackgroundThumbnail(timeoutInMs = 1000): void {
    this.isBackgroundThumbnailAnimatedSig.set(true);

    setTimeout(() => {
      this.isBackgroundThumbnailAnimatedSig.set(false);
    }, timeoutInMs);
  }
}
